/**
 * NotificationsPage — Email, Discord & push notification settings
 *
 * Sources:
 *   GET  /api/email/settings                → current email config
 *   PUT  /api/email/settings                → update email config
 *   POST /api/email/test                    → send test email
 *   GET  /api/notifications/discord/status  → Discord webhook status
 */
import { useEffect, useState, useCallback } from 'react'
import {
  Mail, Bell, Send, Loader2, CheckCircle, AlertTriangle,
  MessageSquare, Smartphone, RefreshCw, Wifi, WifiOff,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '@/lib/api'
import PageHeader from '@/components/ui/PageHeader'

// ── Types ────────────────────────────────────────────────────────────────────

interface EmailSettings {
  email_to: string
  api_key_set: boolean
  briefing_enabled: boolean
  trade_alerts_enabled: boolean
  weekly_report_enabled: boolean
  risk_alerts_enabled: boolean
}

/** Shape of GET /api/notifications/discord/status (webhook-based). */
interface DiscordStatus {
  configured: boolean
  enabled: boolean
  forward_recommendations: boolean
  forward_trades: boolean
  forward_risk: boolean
  forward_watchlist: boolean
  webhook_url_set: boolean
}

// ── Reusable UI ──────────────────────────────────────────────────────────────

function Toggle({ checked, onChange, label, disabled }: {
  checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked ? 'true' : 'false'}
      aria-label={`Toggle ${label}`}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={clsx(
        'relative w-10 h-6 rounded-full transition-colors duration-200 shrink-0',
        'focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2 focus-visible:ring-offset-surface-card',
        'disabled:opacity-40',
        checked ? 'bg-blue' : 'bg-surface-hover',
      )}
    >
      <span
        className={clsx(
          'absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200',
          checked && 'translate-x-4',
        )}
        aria-hidden
      />
    </button>
  )
}

function SettingRow({ label, description, children }: {
  label: string; description?: string; children: React.ReactNode
}) {
  return (
    <div className="flex items-center justify-between px-5 py-3.5 gap-4">
      <div className="min-w-0">
        <p className="text-sm text-text-primary">{label}</p>
        {description && <p className="text-xs text-text-muted mt-0.5">{description}</p>}
      </div>
      <div className="shrink-0 flex items-center gap-2">{children}</div>
    </div>
  )
}

function Section({ title, icon: Icon, badge, children }: {
  title: string; icon: React.ElementType; badge?: string; children: React.ReactNode
}) {
  return (
    <section className="card overflow-hidden" aria-labelledby={`section-${title}`}>
      <div className="px-5 py-4 border-b border-border flex items-center gap-2">
        <Icon size={15} className="text-text-muted" aria-hidden />
        <h2 id={`section-${title}`} className="panel-title">{title}</h2>
        {badge && (
          <span className="ml-auto text-[10px] font-medium px-2 py-0.5 rounded-full bg-surface-hover text-text-muted">
            {badge}
          </span>
        )}
      </div>
      <div className="divide-y divide-border">{children}</div>
    </section>
  )
}

// ── Main Component ───────────────────────────────────────────────────────────

export default function NotificationsPage() {
  // Email state
  const [email, setEmail] = useState<EmailSettings | null>(null)
  const [emailLoading, setEmailLoading] = useState(true)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [testSending, setTestSending] = useState(false)
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null)
  const [dirty, setDirty] = useState(false)

  // Discord state
  const [discord, setDiscord] = useState<DiscordStatus | null>(null)
  const [discordLoading, setDiscordLoading] = useState(true)

  // ── Fetch email settings ───────────────────────────────────────────────
  const fetchEmail = useCallback(async () => {
    setEmailLoading(true)
    setEmailError(null)
    try {
      const res = await api.get<EmailSettings>('/api/email/settings')
      setEmail(res.data)
      setDirty(false)
    } catch (err: any) {
      setEmailError(err?.message ?? 'Failed to load email settings')
    } finally {
      setEmailLoading(false)
    }
  }, [])

  // ── Fetch Discord status ───────────────────────────────────────────────
  const fetchDiscord = useCallback(async () => {
    setDiscordLoading(true)
    try {
      const res = await api.get<DiscordStatus>('/api/notifications/discord/status')
      setDiscord(res.data)
    } catch {
      // Discord may not be configured — not an error
      setDiscord(null)
    } finally {
      setDiscordLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchEmail()
    fetchDiscord()
  }, [fetchEmail, fetchDiscord])

  // ── Save email settings ────────────────────────────────────────────────
  const handleSave = async () => {
    if (!email) return
    setSaving(true)
    try {
      const res = await api.put<EmailSettings>('/api/email/settings', email)
      setEmail(res.data)
      setDirty(false)
    } catch (err: any) {
      setEmailError(err?.message ?? 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  // ── Send test email ────────────────────────────────────────────────────
  const handleTest = async () => {
    setTestSending(true)
    setTestResult(null)
    try {
      await api.post('/api/email/test')
      setTestResult({ ok: true, msg: 'Test email sent successfully' })
    } catch (err: any) {
      setTestResult({ ok: false, msg: err?.response?.data?.detail ?? err?.message ?? 'Failed to send' })
    } finally {
      setTestSending(false)
    }
  }

  // ── Local state mutators ───────────────────────────────────────────────
  const patch = (partial: Partial<EmailSettings>) => {
    if (!email) return
    setEmail({ ...email, ...partial })
    setDirty(true)
  }

  // ── Skeleton ───────────────────────────────────────────────────────────
  if (emailLoading && !email) {
    return (
      <div className="space-y-4 animate-fade-in">
        <div className="skeleton h-8 w-64" />
        <div className="skeleton h-48 rounded-xl" />
        <div className="skeleton h-32 rounded-xl" />
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Notifications & Email"
        icon={Bell}
        subtitle="Configure alerts and delivery"
        actions={dirty && (
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="btn-primary flex items-center gap-2"
          >
            {saving ? <Loader2 size={14} className="animate-spin" aria-hidden /> : <CheckCircle size={14} aria-hidden />}
            Save Changes
          </button>
        )}
      />

      {/* Error banner */}
      {emailError && (
        <div className="card p-4 flex items-center gap-2 border-rose/40 bg-rose/5">
          <AlertTriangle size={14} className="text-rose-light shrink-0" aria-hidden />
          <p className="text-sm text-rose-light">{emailError}</p>
          <button type="button" onClick={fetchEmail} className="ml-auto btn-secondary text-xs flex items-center gap-1">
            <RefreshCw size={12} aria-hidden /> Retry
          </button>
        </div>
      )}

      {/* ── Email Section ─────────────────────────────────────────────────── */}
      <Section title="Email Delivery" icon={Mail} badge={email?.api_key_set ? 'Resend configured' : 'Not configured'}>
        {/* API status */}
        <SettingRow
          label="Resend API"
          description={email?.api_key_set
            ? 'Resend API key is configured and ready'
            : 'Not configured — add RESEND_API_KEY to your environment'}
        >
          <span className={email?.api_key_set ? 'badge-green' : 'badge-amber'}>
            {email?.api_key_set ? 'Active' : 'Missing Key'}
          </span>
        </SettingRow>

        {/* Email address */}
        <SettingRow label="Recipient Email" description="Where notifications are delivered">
          <input
            type="email"
            value={email?.email_to ?? ''}
            onChange={e => patch({ email_to: e.target.value })}
            placeholder="you@example.com"
            aria-label="Recipient email address"
            className="input w-64 px-3 py-1.5"
          />
        </SettingRow>

        {/* Toggle switches */}
        <SettingRow label="Morning Briefing" description="Daily market overview at 7:30 AM">
          <Toggle
            checked={email?.briefing_enabled ?? false}
            onChange={v => patch({ briefing_enabled: v })}
            label="Morning Briefing"
          />
        </SettingRow>

        <SettingRow label="Trade Alerts" description="Real-time alerts when trades are executed">
          <Toggle
            checked={email?.trade_alerts_enabled ?? false}
            onChange={v => patch({ trade_alerts_enabled: v })}
            label="Trade Alerts"
          />
        </SettingRow>

        <SettingRow label="Weekly Report" description="Portfolio summary every Sunday">
          <Toggle
            checked={email?.weekly_report_enabled ?? false}
            onChange={v => patch({ weekly_report_enabled: v })}
            label="Weekly Report"
          />
        </SettingRow>

        <SettingRow label="Risk Alerts" description="Immediate alerts for concentration or drawdown breaches">
          <Toggle
            checked={email?.risk_alerts_enabled ?? false}
            onChange={v => patch({ risk_alerts_enabled: v })}
            label="Risk Alerts"
          />
        </SettingRow>

        {/* Test email */}
        <div className="px-5 py-4 flex items-center gap-3">
          <button
            type="button"
            onClick={handleTest}
            disabled={testSending || !email?.api_key_set}
            className="btn-secondary flex items-center gap-2 text-sm"
          >
            {testSending
              ? <Loader2 size={14} className="animate-spin" aria-hidden />
              : <Send size={14} aria-hidden />}
            Send Test Email
          </button>
          {testResult && (
            <span className={clsx(
              'text-xs flex items-center gap-1',
              testResult.ok ? 'text-emerald-400' : 'text-rose-light',
            )}>
              {testResult.ok
                ? <CheckCircle size={12} aria-hidden />
                : <AlertTriangle size={12} aria-hidden />}
              {testResult.msg}
            </span>
          )}
        </div>
      </Section>

      {/* ── Discord Section ───────────────────────────────────────────────── */}
      <Section
        title="Discord"
        icon={MessageSquare}
        badge={discord?.configured ? 'Connected' : 'Not connected'}
      >
        {discordLoading ? (
          <div className="px-5 py-6 flex items-center justify-center gap-2 text-text-muted text-sm">
            <Loader2 size={14} className="animate-spin" aria-hidden />
            Loading Discord status...
          </div>
        ) : !discord?.configured ? (
          <div className="px-5 py-6 text-center">
            <WifiOff size={20} className="mx-auto text-text-muted mb-2" aria-hidden />
            <p className="text-sm text-text-muted">Discord webhook not configured.</p>
            <p className="text-xs text-text-muted mt-1">Set a webhook URL (DISCORD_WEBHOOK_URL or via settings) to enable.</p>
          </div>
        ) : (
          <>
            <SettingRow label="Webhook" description="Where alerts are delivered">
              <div className="flex items-center gap-2">
                <span className={discord.webhook_url_set ? 'badge-green' : 'badge-amber'}>
                  {discord.webhook_url_set ? 'Webhook set' : 'No webhook'}
                </span>
                {discord.enabled ? (
                  <Wifi size={14} className="text-emerald-400" aria-hidden />
                ) : (
                  <WifiOff size={14} className="text-text-muted" aria-hidden />
                )}
              </div>
            </SettingRow>
            {([
              ['Recommendations', discord.forward_recommendations],
              ['Trades', discord.forward_trades],
              ['Risk alerts', discord.forward_risk],
              ['Watchlist alerts', discord.forward_watchlist],
            ] as const).map(([label, on]) => (
              <SettingRow key={label} label={label} description={on ? 'Forwarding to Discord' : 'Not forwarded'}>
                {on ? (
                  <Wifi size={14} className="text-emerald-400" aria-hidden />
                ) : (
                  <WifiOff size={14} className="text-text-muted" aria-hidden />
                )}
              </SettingRow>
            ))}
          </>
        )}
      </Section>

      {/* ── Push Notifications (Future) ───────────────────────────────────── */}
      <Section title="Push Notifications" icon={Smartphone} badge="Coming soon">
        <div className="px-5 py-6 text-center">
          <Smartphone size={24} className="mx-auto text-text-muted mb-2" aria-hidden />
          <p className="text-sm text-text-muted">Push notifications will be available in a future release.</p>
          <p className="text-xs text-text-muted mt-1">Browser and mobile push for real-time alerts.</p>
        </div>
      </Section>
    </div>
  )
}
